import styled from "styled-components";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Dropdown, Segment } from "semantic-ui-react";
import { BackButton } from "../common/back-button";
import { useSelector, useDispatch } from "react-redux";
import { fetchEstates } from "../../redux/modules/estates";
import { useHistory, useLocation, useParams } from "react-router-dom";
import qs from "qs";
import { Loading } from "../../components/common/loading";
import { getUrlParams } from "../../components/common/urlparams";
import { RoleRequired } from "../auth/role-required";
import { withMedia } from "react-media-query-hoc";
import { PaginationWithQuery } from "../../components/common/pagination";
import {
  ContentFilterFormBody,
  estateListQueryFilterParams,
} from "./estate-content-filter";
import {
  ContentFilterForm,
  ContentFilterPanel,
  ContentFilterTrigger,
  isFiltered,
} from "../../components/common/content-filter";
import { permissions } from "../../constant/permissions";
import { EstateEditModal } from "./estate-edit-modal";
import { DisplayCreatedSettlement } from "./settlement-created";
import { EstateTableComponent } from "./estate-table";
import { Grid } from "../../components/base/elements";
import { DownloadLink } from "../../helpers/download-link";
import { t } from "../../constant/translations";

const StyledControlHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1em 0;
`;

const StyledGrid = styled(Grid)`
  min-height: 100%;
`;

const StyledPaginationWithQuery = styled(PaginationWithQuery)({
  "margin-top": "0!important",
  "margin-bottom": "0!important",
});

const EstateTable = Loading(EstateTableComponent);

const EstateSortButton = () => {
  const location = useLocation();
  const history = useHistory();

  const sortBy = (key) => {
    const params = getUrlParams(location);
    if (key) {
      params.ordering = key;
    } else {
      delete params.ordering;
    }
    history.push({
      pathname: location.pathname,
      search: qs.stringify(params, { addQueryPrefix: true }),
    });
  };

  const sortColumn = getUrlParams(location).ordering;
  const sortIcon =
    sortColumn && sortColumn[0] === "-" ? "sort down" : "sort up";
  const transmap = {
    debt: t("billing.obligations"),
    "-debt": t("billing.obligations"),
    default: t("estate.default_order"),
    name: t("estate.name_order"),
    type: t("estate.type_order"),
  };
  return (
    <Dropdown
      icon={null}
      trigger={
        <Button
          size="tiny"
          content={transmap[sortColumn] || t("estate.default_order")}
          icon={sortIcon}
          basic={true}
        />
      }
      header={t("estate.sorting")}
      options={[
        { key: 1, text: t("estate.default_order"), value: "default" },
        { key: 2, text: t("estate.name_order"), value: "name" },
        { key: 3, text: t("estate.type_order"), value: "type" },
        { key: 4, text: t("estate.desc_liabiliies"), value: "-debt" },
        { key: 5, text: t("estate.asc_liabiliies"), value: "debt" },
      ]}
      value={sortColumn || "default"}
      onChange={(event, data) => {
        sortBy(data.value || null);
      }}
    />
  );
};

const EstateDownloadButton = ({ block }) => {
  const location = useLocation();

  let download = t("estate.properties") + ".csv";
  let queryParams;
  if (block) {
    download = `${block.name}-${download}`;
  }

  queryParams = qs.stringify(
    {
      ...getUrlParams(location),
      block: block && block.id,
      format: "csv",
    },
    { addQueryPrefix: false }
  );
  const url = `/api/estate/?${queryParams}`;

  return (
    <DownloadLink
      href={url}
      target={"_blank"}
      download={download}
      icon={"download"}
      size="tiny"
      basic={true}
    />
  );
};

EstateDownloadButton.propTypes = {
  block: PropTypes.object,
};

const EstateList = ({
  estates,
  loading,
  meta,
  block,
  media,
  user,
  loadEstates,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    loadEstates(getUrlParams(location));
  }, [location.search]);

  const entries = meta ? meta.count : 0;
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  return (
    <StyledGrid centered>
      <Grid.Column textAlign={"left"}>
        <StyledControlHead>
          {(query.profile_id || query.name) && <BackButton size={"tiny"} />}
          <StyledPaginationWithQuery entries={entries} />
          <div>
            <EstateSortButton />
            <ContentFilterTrigger
              onClick={() => setFilterOpen(!filterOpen)}
              queryParamConfig={estateListQueryFilterParams}
              media={media}
            />
            {block && (
              <RoleRequired staff={true} permission={permissions.estateEdit}>
                <EstateEditModal
                  initialData={{ charging_enabled: true, block_id: block.id }}
                  buttonContent={
                    media.handheld ? t("estate.new_property") : null
                  }
                />
              </RoleRequired>
            )}
            <DisplayCreatedSettlement />
            <EstateDownloadButton block={block} />
          </div>
        </StyledControlHead>
        <ContentFilterPanel
          open={filterOpen}
          onApply={() => {
            setFilterOpen(false);
          }}
          queryParamConfig={estateListQueryFilterParams}
          FormBody={ContentFilterFormBody}
        >
          <EstateTable
            estates={estates}
            loading={loading}
            blockId={block ? block.id : null}
            user={user}
            media={media}
          />
          {!loading && <StyledPaginationWithQuery entries={entries} />}
        </ContentFilterPanel>
      </Grid.Column>
    </StyledGrid>
  );
};

EstateList.propTypes = {
  estates: PropTypes.array,
  loading: PropTypes.bool,
  meta: PropTypes.object,
  block: PropTypes.object,
  media: PropTypes.object,
  user: PropTypes.object,
  loadEstates: PropTypes.func.isRequired,
};

const CenterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 800px) {
    > div {
      max-width: 800px;
    }
  }

  @media (min-width: 100px) {
    padding: 1em 1em;
    > div {
      width: 100%;
    }
  }
`;

const BlockEstateListComponent = (props) => {
  const estates = useSelector((state) => state.estates.entities);
  const meta = useSelector((state) => state.estates.meta);
  const loading = useSelector((state) => state.estates.pending);
  const user = useSelector((state) => state.auth.user);
  const block = useSelector((state) => state.blocks.selected);
  const dispatch = useDispatch();
  const params = useParams();

  const loadEstates = (urlParams) => {
    return dispatch(fetchEstates({ block: params.blockId, ...urlParams }));
  };

  return (
    <EstateList
      {...props}
      estates={estates}
      meta={meta}
      loading={loading}
      user={user}
      block={block}
      loadEstates={loadEstates}
    />
  );
};

const GlobalEstateListComponent = (props) => {
  const estates = useSelector((state) => state.estates.entities);
  const meta = useSelector((state) => state.estates.meta);
  const loading = useSelector((state) => state.estates.pending);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const loadEstates = (params) => {
    const query = { ...params };
    if (!query.q) {
      query.active_only = true;
    }
    return dispatch(fetchEstates(query));
  };

  if (!isFiltered(estateListQueryFilterParams)) {
    return (
      <CenterContent>
        <Segment>
          <h2> {t("estate.property_search")} </h2>
          <ContentFilterForm
            queryParamConfig={estateListQueryFilterParams}
            FormBody={ContentFilterFormBody}
          />
        </Segment>
      </CenterContent>
    );
  }

  return (
    <EstateList
      {...props}
      estates={estates}
      meta={meta}
      loading={loading}
      user={user}
      loadEstates={loadEstates}
    />
  );
};

export const BlockEstateList = withMedia(BlockEstateListComponent);
export const GlobalEstateList = withMedia(GlobalEstateListComponent);
